import React from 'react';

interface FunnelImageProps {
  src: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  loading?: 'eager' | 'lazy';
}

/**
 * Imagen con fallback a render alternativo si falla la carga.
 *
 * Sirve la variante WebP automáticamente solo para imágenes bajo
 * /images/breeds/, que son las únicas que tienen un .webp generado por
 * scripts/optimize-breed-images.cjs. Si activamos `<picture>` con
 * `<source srcSet=*.webp>` para rutas SIN webp generado (funnel/, etc.),
 * el navegador intenta decodificar el HTML del SPA fallback como webp,
 * falla, y la imagen desaparece — bug visto el 2026-04-28 con las fotos
 * de condición corporal y actividad.
 */
const HAS_WEBP_VARIANT = /\/images\/breeds\/[^/]+\.png$/i;

export const FunnelImage: React.FC<FunnelImageProps> = ({ src, alt, className, fallback = null, loading = 'lazy' }) => {
  const [error, setError] = React.useState(false);
  React.useEffect(() => {
    setError(false);
  }, [src]);
  if (error) return <>{fallback}</>;

  const webpSrc = HAS_WEBP_VARIANT.test(src) ? src.replace(/\.png$/i, '.webp') : null;

  if (webpSrc) {
    return (
      <picture>
        <source srcSet={webpSrc} type="image/webp" />
        <img
          src={src}
          alt={alt}
          className={className}
          loading={loading}
          decoding="async"
          onError={() => setError(true)}
        />
      </picture>
    );
  }

  return (
    <img src={src} alt={alt} className={className} loading={loading} decoding="async" onError={() => setError(true)} />
  );
};
